import { importFailureMessage, importMapDocument } from './import.ts';
import { CURRENT_MAP_VERSION, migrateMapDocument } from './migrate.ts';
import type { MapDocument } from './types.ts';

export type ParseMapResult = { map: MapDocument } | { error: string };

export function serializeMap(map: MapDocument): string {
  return JSON.stringify({ ...map, version: CURRENT_MAP_VERSION });
}

/**
 * Returns null instead of throwing so callers can surface a friendly error. Older schema versions
 * are migrated up, so the returned document is always current.
 */
export function parseMapDocument(json: string): MapDocument | null {
  const result = parseMapDocumentOrReason(json);
  return 'map' in result ? result.map : null;
}

/** Same loader as `parseMapDocument`, with a player-facing reason when the file is rejected. */
export function parseMapDocumentOrReason(json: string): ParseMapResult {
  const result = importMapDocument(json);
  return result.ok ? { map: result.map } : { error: importFailureMessage(result) };
}

/** True for any version this build can load, not just the current one. */
export function isMapDocument(value: unknown): boolean {
  return migrateMapDocument(value) !== null;
}

export function isCurrentMapDocument(value: unknown): value is MapDocument {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { version?: unknown }).version === CURRENT_MAP_VERSION
  );
}

export function cloneMapDocument(map: MapDocument): MapDocument {
  if (typeof structuredClone === 'function') {
    return structuredClone(map);
  }

  return JSON.parse(JSON.stringify(map)) as MapDocument;
}
